import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import {
  Archive,
  ArrowsClockwise,
  CaretDown,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  CurrencyDollar,
  DotsThreeVertical,
  DownloadSimple,
  Eye,
  FileText,
  Funnel,
  Info,
  Lightning,
  MagnifyingGlass,
  Shield,
  Target,
  Timer,
  Tray as InboxIcon,
  TrendUp,
  WarningCircle,
  X
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

/**
 * Work Tray - AI Outcomes Signal Layer
 * 
 * The Tray is not a log of everything AI does.
 * It's a signal for outcomes that deserve attention.
 * 
 * Mental Model: AI thinks → Human decides → Systems act
 */
const Tray = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (activeOrganization) {
      fetchInboxItems();
    }
  }, [activeOrganization]);

  const fetchInboxItems = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/inbox/items`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching inbox items:', err);
      toast({
        title: 'Error',
        description: 'Failed to load inbox items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Funnel items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Status filter
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      
      // Priority filter
      if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
      
      // Category filter
      if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
      
      // MagnifyingGlass query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = item.title?.toLowerCase().includes(query);
        const matchesSummary = item.summary?.toLowerCase().includes(query);
        if (!matchesTitle && !matchesSummary) return false;
      }
      
      return true;
    });
  }, [items, statusFilter, priorityFilter, categoryFilter, searchQuery]);

  // Get unique categories from items
  const categories = useMemo(() => {
    const cats = new Set(items.map(item => item.category).filter(Boolean));
    return Array.from(cats);
  }, [items]);

  // Update item status
  const handleStatusUpdate = async (itemId, newStatus) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/inbox/items/${itemId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus })
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        // Update local state
        setItems(prev => prev.map(item =>
          item.id === itemId ? { ...item, status: newStatus } : item
        ));
        
        toast({
          title: 'Status Updated',
          description: `Item marked as ${newStatus}`,
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  // Delete/dismiss item
  const handleDismiss = async (itemId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/inbox/items/${itemId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );

      if (response.ok) {
        setItems(prev => prev.filter(item => item.id !== itemId));
        toast({
          title: 'Item Dismissed',
          description: 'Removed from inbox',
          className: 'bg-[#1a1a1a] border-[rgba(255,255,255,0.2)] text-white'
        });
      }
    } catch (err) {
      console.error('Error dismissing item:', err);
    }
  };

  // Handle view action
  const handleView = (item) => {
    // Mark as reviewed if unread
    if (item.status === 'unread') {
      handleStatusUpdate(item.id, 'reviewed');
    }
    
    // Navigate based on item type
    if (item.notification_type === 'app_review' || item.related_object_type === 'app_submission') {
      navigate('/my-app-submissions');
    } else if (item.notification_type === 'submission_review' || item.related_object_type === 'marketplace_submission') {
      navigate('/my-submissions');
    } else if (item.session_id) {
      navigate(`/partner-pro/workspace/sessions/${item.session_id}`);
    }
  };

  // Get impact icon and display
  const getImpactDisplay = (impact) => {
    if (!impact) return null;
    
    const { type, estimated_value, confidence } = impact;
    
    let Icon = TrendUp;
    let color = 'text-blue-400';
    let displayValue = '';
    
    switch (type) {
      case 'financial':
        Icon = CurrencyDollar;
        color = 'text-green-400';
        displayValue = estimated_value ? `$${estimated_value.toLocaleString()}` : '';
        break;
      case 'time':
        Icon = Timer;
        color = 'text-purple-400';
        displayValue = estimated_value ? `${estimated_value}h saved` : '';
        break;
      case 'risk':
        Icon = WarningCircle;
        color = 'text-red-400';
        displayValue = confidence === 'high' ? 'High Risk' : 'Risk Detected';
        break;
      case 'compliance':
        Icon = Shield;
        color = 'text-orange-400';
        displayValue = 'Compliance';
        break;
      default:
        displayValue = '';
    }
    
    return { Icon, color, displayValue };
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'unread':
        return { color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', label: 'Not reviewed' };
      case 'reviewed':
        return { color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', label: 'Reviewed' };
      case 'actioned':
        return { color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', label: 'Actioned' };
      case 'dismissed':
        return { color: 'text-gray-400', bg: 'bg-gray-500/20', border: 'border-gray-500/30', label: 'Dismissed' };
      default:
        return { color: 'text-gray-400', bg: 'bg-gray-500/20', border: 'border-gray-500/30', label: status };
    }
  };

  // Get priority styling
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-4 border-l-red-500';
      case 'medium':
        return 'border-l-4 border-l-yellow-500';
      case 'low':
        return 'border-l-4 border-l-gray-500';
      default:
        return '';
    }
  };

  // Format relative time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-[#6366F1]/20 rounded-lg">
                <InboxIcon className="w-6 h-6 text-[#6366F1]" />
              </div>
              Tray
            </h1>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchInboxItems(true)}
            disabled={refreshing}
            className="border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.05)]"
          >
            <ArrowsClockwise className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Subtitle */}
        <p className="text-gray-400 mb-6 flex items-center gap-2">
          Your AI work signal — outcomes that need human awareness or action.
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info size={14} className="text-gray-500 hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)] text-gray-300 max-w-xs">
                <p>The Tray is not a log of everything AI does. It&apos;s a signal for outcomes that deserve attention.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </p>

        {/* Filters Bar */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          {/* MagnifyingGlass */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[rgba(255,255,255,0.1)] rounded-lg text-white text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>
          
          {/* Status Funnel */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="border-[rgba(255,255,255,0.1)] text-gray-300">
                <Funnel className="w-4 h-4 mr-2" />
                {statusFilter === 'all' ? 'All Status' : getStatusInfo(statusFilter).label}
                <CaretDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
              <DropdownMenuItem onClick={() => setStatusFilter('all')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                All Status
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('unread')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                Not reviewed
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('reviewed')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                Reviewed
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('actioned')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                Actioned
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Priority Funnel */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="border-[rgba(255,255,255,0.1)] text-gray-300">
                Priority: {priorityFilter === 'all' ? 'All' : priorityFilter}
                <CaretDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
              <DropdownMenuItem onClick={() => setPriorityFilter('all')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                All Priorities
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPriorityFilter('high')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                <span className="w-2 h-2 rounded-full bg-red-500 mr-2" /> High
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPriorityFilter('medium')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2" /> Medium
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPriorityFilter('low')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                <span className="w-2 h-2 rounded-full bg-gray-500 mr-2" /> Low
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Category Funnel */}
          {categories.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-[rgba(255,255,255,0.1)] text-gray-300">
                  {categoryFilter === 'all' ? 'All Categories' : categoryFilter}
                  <CaretDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
                <DropdownMenuItem onClick={() => setCategoryFilter('all')} className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]">
                  All Categories
                </DropdownMenuItem>
                {categories.map(cat => (
                  <DropdownMenuItem 
                    key={cat} 
                    onClick={() => setCategoryFilter(cat)} 
                    className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]"
                  >
                    {cat}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Items List */}
        {filteredItems.length === 0 ? (
          // Empty State
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center">
              <InboxIcon className="w-10 h-10 text-[#6366F1]/50" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Nothing needs your attention right now
            </h3>
            <p className="text-gray-400 max-w-md mx-auto mb-6">
              When agents produce high-impact results, recommendations, or actions that need review, they&apos;ll appear here.
            </p>
            <p className="text-sm text-gray-500">
              Most AI work runs quietly in the background.<br />
              This Tray is for what matters.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map(item => {
              const statusInfo = getStatusInfo(item.status);
              const impactDisplay = getImpactDisplay(item.impact);
              const priorityStyle = getPriorityStyle(item.priority);
              
              return (
                <div
                  key={item.id}
                  className={`group bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden transition-all hover:border-[#6366F1]/30 ${priorityStyle} ${
                    item.status === 'unread' ? 'bg-[#111111]' : 'bg-[#0D0D0D]'
                  }`}
                >
                  <div className="p-4 flex items-start gap-4">
                    {/* Impact Icon */}
                    {impactDisplay ? (
                      <div className={`p-2 rounded-lg bg-[rgba(255,255,255,0.05)]`}>
                        <impactDisplay.Icon className={`w-5 h-5 ${impactDisplay.color}`} />
                      </div>
                    ) : (
                      <div className="p-2 rounded-lg bg-[rgba(255,255,255,0.05)]">
                        <Target className="w-5 h-5 text-gray-400" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {/* Title */}
                          <h3 className={`font-medium mb-1 ${item.status === 'unread' ? 'text-white' : 'text-gray-300'}`}>
                            {item.title}
                          </h3>
                          
                          {/* Summary */}
                          <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                            {item.summary}
                          </p>
                          
                          {/* Meta Row */}
                          <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatTime(item.created_at)}
                            </span>
                            
                            <span className="text-gray-600">·</span>
                            
                            <span>{item.category}</span>
                            
                            <span className="text-gray-600">·</span>
                            
                            {/* Status Badge */}
                            <span className={`px-2 py-0.5 rounded ${statusInfo.bg} ${statusInfo.color} border ${statusInfo.border}`}>
                              {statusInfo.label}
                            </span>
                            
                            {/* Impact Value */}
                            {impactDisplay?.displayValue && (
                              <>
                                <span className="text-gray-600">·</span>
                                <span className={impactDisplay.color}>
                                  {impactDisplay.displayValue}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(item)}
                            className="text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)]"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          
                          {/* More Actions */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)]"
                              >
                                <DotsThreeVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
                              {item.status !== 'reviewed' && (
                                <DropdownMenuItem
                                  onClick={() => handleStatusUpdate(item.id, 'reviewed')}
                                  className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" /> Mark as Reviewed
                                </DropdownMenuItem>
                              )}
                              {item.status !== 'actioned' && (
                                <DropdownMenuItem
                                  onClick={() => handleStatusUpdate(item.id, 'actioned')}
                                  className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]"
                                >
                                  <Lightning className="w-4 h-4 mr-2" /> Mark as Actioned
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDismiss(item.id)}
                                className="text-gray-300 focus:bg-[rgba(255,255,255,0.05)]"
                              >
                                <Archive className="w-4 h-4 mr-2" /> Dismiss
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tray;
